import React from 'react';
import Field from '../Field';
import AutoTextarea from '../AutoTextarea';
import { geoStatus } from '../mapFlag';

/**
 * list_item: one entry of a "top N" list. A run of consecutive list_item
 * sections is what the site renders as a list of cards, so each card is its own
 * section -- add, remove and reorder them with the section controls around this
 * form. Nothing here is required; whichever parts are filled in get rendered.
 */
export default function ListItemEditor({ idPrefix, data, onChange, onPickImage }) {
  const d = data || {};
  const set = (patch) => onChange({ ...d, ...patch });
  const image = d.image && typeof d.image === 'object' ? d.image : null;
  const link = d.link && typeof d.link === 'object' ? d.link : null;
  const setImage = (patch) => set({ image: { ...(image || {}), ...patch } });
  const setLink = (patch) => set({ link: { ...(link || {}), ...patch } });
  const geo = d.geo && typeof d.geo === 'object' ? d.geo : null;
  // Typing a coordinate makes the point hand-set; the API pins it to the
  // current address line and never looks that address up over it.
  const setGeo = (patch) => set({ geo: { ...(geo || {}), ...patch, source: 'manual' } });
  const coord = (v) => (v === null || v === undefined ? '' : v);

  return (
    <>
      <Field id={`${idPrefix}-title`} label="Entry title" hint="The card's heading, e.g. a place, product or tip.">
        <input id={`${idPrefix}-title`} type="text" className="input" value={d.title || ''} onChange={(e) => set({ title: e.target.value })} placeholder="Zion National Park" />
      </Field>

      <div className="field-row">
        <Field id={`${idPrefix}-is_ordered`} label="List style" hint="Ordered entries show their rank in the heading.">
          <select
            id={`${idPrefix}-is_ordered`}
            className="select"
            value={d.is_ordered ? 'ordered' : 'unordered'}
            onChange={(e) => set({ is_ordered: e.target.value === 'ordered' })}
          >
            <option value="unordered">Unordered — no number</option>
            <option value="ordered">Ordered — numbered</option>
          </select>
        </Field>
        <Field id={`${idPrefix}-position`} label="Rank" hint="Leave empty for no number.">
          <input
            id={`${idPrefix}-position`}
            type="number"
            min="1"
            className="input"
            value={d.position === null || d.position === undefined ? '' : d.position}
            onChange={(e) => set({ position: e.target.value === '' ? null : parseInt(e.target.value, 10) || null })}
          />
        </Field>
      </div>

      <Field id={`${idPrefix}-introduction`} label="Introduction" hint="Markdown. Shown first, above the longer description.">
        <AutoTextarea id={`${idPrefix}-introduction`} value={d.introduction || ''} onChange={(e) => set({ introduction: e.target.value })} placeholder="One or two lines…" minRows={2} />
      </Field>

      <Field id={`${idPrefix}-description`} label="Description" hint="Markdown. The body of the card.">
        <AutoTextarea id={`${idPrefix}-description`} value={d.description || ''} onChange={(e) => set({ description: e.target.value })} placeholder="The detail…" minRows={4} />
      </Field>

      <fieldset style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: 10, margin: '0 0 12px' }}>
        <legend style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-muted)', padding: '0 4px' }}>Image (optional)</legend>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 10 }}>
          {image && image.src ? (
            <img src={image.src} alt={image.alt || ''} style={{ maxWidth: 160, maxHeight: 110, borderRadius: 'var(--radius)', border: '1px solid var(--color-border)' }} />
          ) : null}
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => onPickImage((picked) => setImage({ media_id: picked._id, alt: (image && image.alt) || picked.description || picked.name || '' }))}>
            {image && image.media_id ? 'Change library image' : 'Choose from library'}
          </button>
          {image && (
            <button type="button" className="btn btn-ghost btn-sm btn-danger-text" onClick={() => set({ image: null })}>Remove image</button>
          )}
        </div>
        <Field id={`${idPrefix}-image-src`} label="Image URL">
          <input id={`${idPrefix}-image-src`} type="text" className="input" value={(image && image.src) || ''} onChange={(e) => setImage({ src: e.target.value })} placeholder="https://…" />
        </Field>
        <div className="field-row">
          <Field id={`${idPrefix}-image-alt`} label="Alt text">
            <input id={`${idPrefix}-image-alt`} type="text" className="input" value={(image && image.alt) || ''} onChange={(e) => setImage({ alt: e.target.value })} placeholder="Describe the image" />
          </Field>
          <Field id={`${idPrefix}-image-caption`} label="Caption">
            <input id={`${idPrefix}-image-caption`} type="text" className="input" value={(image && image.caption) || ''} onChange={(e) => setImage({ caption: e.target.value })} />
          </Field>
        </div>
      </fieldset>

      <fieldset style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: 10, margin: '0 0 12px' }}>
        <legend style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-muted)', padding: '0 4px' }}>Map pin</legend>
        <p className="hint" style={{ margin: '0 0 8px' }}>
          The pin for the address on the description's first line, looked up when that line changes. {geoStatus(geo)}
        </p>
        <div className="field-row">
          <Field id={`${idPrefix}-geo-lat`} label="Latitude" hint="-90 to 90, e.g. 33.757236">
            <input id={`${idPrefix}-geo-lat`} type="text" inputMode="decimal" className="input mono" value={coord(geo && geo.lat)} onChange={(e) => setGeo({ lat: e.target.value.trim() })} placeholder="33.757236" />
          </Field>
          <Field id={`${idPrefix}-geo-lng`} label="Longitude" hint="-180 to 180, e.g. -84.354115">
            <input id={`${idPrefix}-geo-lng`} type="text" inputMode="decimal" className="input mono" value={coord(geo && geo.lng)} onChange={(e) => setGeo({ lng: e.target.value.trim() })} placeholder="-84.354115" />
          </Field>
        </div>
        {geo && (
          <button type="button" className="btn btn-ghost btn-sm btn-danger-text" onClick={() => set({ geo: null })}>Clear and look the address up again</button>
        )}
      </fieldset>

      <fieldset style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: 10, margin: '0 0 12px' }}>
        <legend style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-muted)', padding: '0 4px' }}>Link to a page (optional)</legend>
        <Field id={`${idPrefix}-link-slug`} label="Page slug" hint="The page id as stored: a nested path keeps its %2F, e.g. us-national-parks%2Fzion.">
          <input id={`${idPrefix}-link-slug`} type="text" className="input mono" value={(link && link.slug) || ''} onChange={(e) => setLink({ slug: e.target.value })} placeholder="us-national-parks%2Fzion" />
        </Field>
        <div className="field-row">
          <Field id={`${idPrefix}-link-label`} label="Link text" hint="Falls back to the page title.">
            <input id={`${idPrefix}-link-label`} type="text" className="input" value={(link && link.label) || ''} onChange={(e) => setLink({ label: e.target.value })} placeholder="Full guide" />
          </Field>
          <Field id={`${idPrefix}-link-title`} label="Page title">
            <input id={`${idPrefix}-link-title`} type="text" className="input" value={(link && link.title) || ''} onChange={(e) => setLink({ title: e.target.value })} />
          </Field>
        </div>
        {link && (
          <button type="button" className="btn btn-ghost btn-sm btn-danger-text" onClick={() => set({ link: null })}>Remove link</button>
        )}
      </fieldset>
    </>
  );
}
